import { CheckOutlined, SendOutlined } from '@ant-design/icons';
import {
  AnagraficaDelegatoDTO,
  GetFrontAnagraficheDelegatiParams,
  WsRispostaPaginataListAnagraficaDelegatoDTO,
} from '@framework/api-client';
import { useField } from '@framework/form';
import { t } from '@framework/i18n';
import { TableData } from '@framework/ui';
import { Button, Input, Space } from 'antd';
import { debounce } from 'lodash';
import React, { useMemo } from 'react';
import { modalAddressBookColumns } from '../addressBookColumns';
import { IAddressBookSelectInputOnSelect } from './AddressBookButton';

function isSelected(
  selected: string[] | undefined,
  record: AnagraficaDelegatoDTO,
) {
  if (!selected) return false;
  return selected.includes(record.codiceFiscale ?? record.email ?? '<unknown>');
}

export interface IAddressBookModalTableProps {
  query: GetFrontAnagraficheDelegatiParams;
  setQuery: React.Dispatch<
    React.SetStateAction<GetFrontAnagraficheDelegatiParams>
  >;
  data: WsRispostaPaginataListAnagraficaDelegatoDTO | undefined;
  isLoading: boolean;
  isFetching: boolean;
  onSelect: IAddressBookSelectInputOnSelect;
  setIsOpen: (isOpen: boolean) => void;
  selected?: string[];
  idFieldName: string;
}

export function AddressBookModalTable(props: IAddressBookModalTableProps) {
  const {
    query,
    setQuery,
    data,
    isLoading,
    isFetching,
    onSelect,
    setIsOpen,
    selected,
    idFieldName,
  } = props;

  const field = useField(idFieldName);

  const { records, meta } = useMemo(
    () => ({ records: data?.dati ?? [], meta: data?.meta }),
    [data],
  );

  return (
    <>
      <Space style={{ width: '100%', marginBottom: '12px' }}>
        <Input
          placeholder={t(
            'address-book.input.fiscal-code.placeholder',
            'Codice Fiscale/Email',
          )}
          suffix={<span aria-hidden="true" />}
          aria-label={t(
            'address-book.input.fiscal-code.aria-label',
            'Ricerca Codice Fiscale/Email',
          )}
          onChange={debounce((e: React.ChangeEvent<HTMLInputElement>) => {
            setQuery({ ...query, primarySearchField: e.target.value });
          }, 500)}
        />
        <Input
          placeholder={t(
            'address-book.input.name.placeholder',
            'Nome/cognome...',
          )}
          suffix={<span aria-hidden="true" />}
          aria-label={t(
            'address-book.input.name.aria-label',
            'Ricerca per altri campi',
          )}
          onChange={debounce((e: React.ChangeEvent<HTMLInputElement>) => {
            setQuery({ ...query, secondarySearchField: e.target.value });
          }, 500)}
        />
      </Space>
      <TableData
        showSorterTooltip={false}
        size="small"
        dataSource={records}
        loading={isLoading || isFetching}
        pagination={{
          current: Number(query.pagina ?? 0) + 1,
          pageSize: query.limite != null ? Number(query.limite) : 10,
          total: meta?.totale ?? 0,
          pageSizeOptions: [5, 10, 20, 50],
        }}
        onChange={pagination => {
          setQuery(query => ({
            ...query,
            limite: pagination.pageSize,
            pagina: (pagination.current ?? 1) - 1,
          }));
        }}
        columns={[
          ...modalAddressBookColumns,
          {
            //   title: 'Azioni',
            key: 'actions',
            render: (record: AnagraficaDelegatoDTO) => (
              <Button
                type="link"
                onClick={() => {
                  onSelect(record, () => setIsOpen(false), field.setValue);
                }}
                icon={
                  isSelected(selected, record) ? (
                    <CheckOutlined />
                  ) : (
                    <SendOutlined />
                  )
                }
                disabled={isSelected(selected, record)}
              >
                {isSelected(selected, record)
                  ? t('address-book.already-selected', 'Selezionato')
                  : t('address-book.select', 'Seleziona')}
              </Button>
            ),
          },
        ]}
      />
    </>
  );
}
